import { Button, Checkbox, Fieldset, Group, Select, Stack, Text, TextInput } from '@mantine/core';
import { useState } from 'react';
import { useCloseModal } from '@ayocore/platform/client/components/ModalHost/ModalContext';
import type { SalesSummaryResult, SalesSummarySettings } from '../common/types';

interface Props extends Record<string, unknown> {
  settings: SalesSummarySettings;
  options: SalesSummaryResult | null;
  onSave: (settings: SalesSummarySettings) => Promise<boolean>;
  onReset: () => Promise<boolean>;
}

/** Локальный черновик: изменения применяются одним действием, без запросов на каждое поле. */
export function SalesSummarySettingsForm({ settings, options, onSave, onReset }: Props) {
  const [draft, setDraft] = useState(settings);
  const { close } = useCloseModal();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Контент глобальной модалки хранит собственное состояние запроса.
  const apply = async (save: () => Promise<boolean>) => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      if (await save()) close();
      else setError('Не удалось сохранить настройки');
    } catch {
      setError('Не удалось сохранить настройки');
    } finally {
      setSaving(false);
    }
  };
  const invalidRange =
    draft.period === 'custom' && (!draft.from || !draft.to || draft.from > draft.to);
  return (
    <form
      onSubmit={event => {
        event.preventDefault();
        if (!invalidRange && !saving) void apply(() => onSave(draft));
      }}
    >
      <Fieldset variant='unstyled' disabled={saving}>
        <Stack gap='sm'>
          <Select
            label='Период'
            value={draft.period}
            allowDeselect={false}
            data={[
              { value: 'month', label: 'Месяц' },
              { value: 'quarter', label: 'Квартал' },
              { value: 'custom', label: 'Произвольный период' },
            ]}
            onChange={value => {
              if (value === 'month' || value === 'quarter' || value === 'custom')
                setDraft({ ...draft, period: value });
            }}
          />
          {draft.period === 'custom' ? (
            <Group grow align='flex-start'>
              <TextInput
                type='date'
                label='С'
                required
                value={draft.from}
                onChange={event => setDraft({ ...draft, from: event.currentTarget.value })}
              />
              <TextInput
                type='date'
                label='По'
                required
                min={draft.from || undefined}
                value={draft.to}
                onChange={event => setDraft({ ...draft, to: event.currentTarget.value })}
              />
            </Group>
          ) : (
            <TextInput
              type='date'
              label='Дата внутри выбранного периода'
              description='Оставьте пустой для текущего месяца или квартала'
              value={draft.referenceDate}
              onChange={event => setDraft({ ...draft, referenceDate: event.currentTarget.value })}
            />
          )}
          <Select
            label='Область данных'
            description='Мои — я ответственный за сделку или менеджер клиента'
            value={draft.scope}
            allowDeselect={false}
            data={[
              { value: 'mine', label: 'Мои сделки' },
              { value: 'branch', label: 'Филиал' },
              { value: 'all', label: 'Все сделки' },
            ]}
            onChange={value => {
              if (value === 'mine' || value === 'branch' || value === 'all')
                setDraft({ ...draft, scope: value, branchId: null, managerId: null });
            }}
          />
          {draft.scope === 'branch' && (
            <Select
              label='Филиал'
              placeholder='Мои филиалы'
              clearable
              value={draft.branchId == null ? null : String(draft.branchId)}
              data={(options?.branches ?? []).map(branch => ({
                value: String(branch.id),
                label: branch.name,
              }))}
              onChange={value => setDraft({ ...draft, branchId: value ? Number(value) : null })}
            />
          )}
          {draft.scope !== 'mine' && (
            <Select
              label='Менеджер'
              placeholder='Все менеджеры выбранной области'
              clearable
              searchable
              value={draft.managerId == null ? null : String(draft.managerId)}
              data={(options?.managers ?? []).map(manager => ({
                value: String(manager.id),
                label: manager.name,
              }))}
              onChange={value => setDraft({ ...draft, managerId: value ? Number(value) : null })}
            />
          )}
          <Checkbox
            label='Сравнивать с предыдущим периодом'
            checked={draft.compare}
            onChange={event => setDraft({ ...draft, compare: event.currentTarget.checked })}
          />
          <Text size='xs' c='dimmed'>
            Только базовая валюта CRM. Настройки сохраняются в вашем профиле.
          </Text>
          {error && (
            <Text role='alert' c='red' size='sm'>
              {error}
            </Text>
          )}
          <Group justify='flex-end'>
            <Button type='button' variant='default' onClick={close}>
              Отмена
            </Button>
            <Button type='submit' loading={saving} disabled={Boolean(invalidRange)}>
              Применить
            </Button>
          </Group>
          <Button
            type='button'
            variant='subtle'
            disabled={saving}
            onClick={() => void apply(onReset)}
          >
            Вернуть общие настройки
          </Button>
        </Stack>
      </Fieldset>
    </form>
  );
}
